/**
 * TasteAuraBackground
 *
 * Animated gradient background based on user's favorite cuisines.
 * Creates a personalized "Taste Aura" effect for the profile header.
 */
import { useMemo } from 'react'
import { Box, BoxProps } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import {
  generateTasteAuraGradient,
  generateCuisineGlow,
  getDominantCuisineColor,
  hexToRgba,
  FavoriteCuisine,
} from '@/utils/cuisineColorMapping'

const MotionBox = motion.create(Box)

export interface TasteAuraBackgroundProps extends Omit<BoxProps, 'children'> {
  /** User's favorite cuisines for color generation */
  cuisines?: FavoriteCuisine[]
  /** Whether to animate the gradient */
  animated?: boolean
  /** Children to render on top of the background */
  children?: React.ReactNode
}

export function TasteAuraBackground({
  cuisines = [],
  animated = true,
  children,
  ...boxProps
}: TasteAuraBackgroundProps) {
  // Generate gradient based on user's cuisines
  const gradient = useMemo(() => generateTasteAuraGradient(cuisines), [cuisines])
  const borderColor = useMemo(
    () => hexToRgba(getDominantCuisineColor(cuisines), 0.4),
    [cuisines]
  )

  return (
    <Box
      position="relative"
      overflow="hidden"
      borderWidth="1px"
      borderColor={borderColor}
      {...boxProps}
    >
      {/* Animated gradient background - GPU accelerated */}
      <MotionBox
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        background={gradient}
        backgroundSize={animated ? '400% 400%' : '100% 100%'}
        transform="translateZ(0)"
        willChange="transform"
        initial={{ backgroundPosition: '0% 50%' }}
        animate={
          animated
            ? {
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }
            : undefined
        }
        transition={{
          duration: 15,
          ease: 'easeInOut',
          repeat: Infinity,
          repeatType: 'loop',
        }}
        zIndex={0}
      />

      {/* Subtle fade overlay at the bottom */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        height="60%"
        background="linear-gradient(to top, var(--color-bg-primary) 0%, transparent 100%)"
        zIndex={1}
        pointerEvents="none"
      />

      {/* Noise texture overlay for depth */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={0.05}
        backgroundImage="url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E')"
        zIndex={2}
        pointerEvents="none"
      />

      {/* Content layer */}
      <Box position="relative" zIndex={3}>
        {children}
      </Box>
    </Box>
  )
}

/**
 * Compact version for smaller UI elements (cards, snippets)
 */
export function TasteAuraGlow({
  cuisines = [],
  intensity = 0.2,
  children,
  ...boxProps
}: {
  cuisines?: FavoriteCuisine[]
  intensity?: number
  children?: React.ReactNode
} & BoxProps) {
  const glowShadow = useMemo(() => generateCuisineGlow(cuisines, intensity), [cuisines, intensity])

  return (
    <Box position="relative" boxShadow={glowShadow} {...boxProps}>
      {children}
    </Box>
  )
}
